import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import s from './HelpIcon.module.scss'

const WIDTH = 320
const MARGIN = 12

/**
 * Ab wann nach OBEN aufgeklappt wird.
 *
 * Die Höhe der Erklärung steht beim Messen noch nicht fest — sie wird erst
 * danach gerendert. Statt zweimal zu messen genügt eine Schwelle: Bleiben unter
 * dem Zeichen weniger als so viele Pixel und ist darüber mehr Platz, klappt die
 * Erklärung nach oben.
 *
 * Aufgefallen an der Kursleiste einer Handelsidee: Deren Erklärung ist 474
 * Pixel hoch und stand zur Hälfte außerhalb des Fensters.
 */
const FLIP_BELOW = 260

interface Props {
  /** Worüber die Erklärung spricht — steht fett über dem Text. */
  term: string
  children: ReactNode
}

/**
 * Das Fragezeichen, das eine Kurzbeschreibung zeigt.
 *
 * Bewusst ein eigenes Element neben der Beschriftung und nicht die
 * Beschriftung selbst: Ein Spaltenkopf sortiert, und beides auf demselben Ziel
 * käme sich in die Quere — wer die Erklärung lesen will, hätte beim Hinsteuern
 * schon umsortiert.
 *
 * Öffnet auf Überfahren UND auf Antippen. Nur Überfahren wäre am Handy nichts
 * wert: Dort gibt es kein Überfahren, und die Erklärung bliebe unerreichbar.
 */
export function HelpIcon({ term, children }: Props) {
  //: `top` ODER `bottom` — nie beides. Nach oben aufgeklappt wird am UNTEREN
  //: Rand verankert, damit die Erklärung nach oben wächst statt nach unten.
  const [place, setPlace] = useState<
    { left: number; top?: number; bottom?: number } | null>(null)
  const [pinned, setPinned] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)

  function measure() {
    const box = anchor.current?.getBoundingClientRect()
    if (!box) return
    // Nach links ausrichten, wenn rechts kein Platz mehr ist. Ohne das steht
    // die Erklärung einer rechten Spalte halb außerhalb des Fensters.
    const left = Math.max(MARGIN,
      Math.min(box.left, window.innerWidth - WIDTH - MARGIN))

    // Und nach oben, wenn unten kein Platz mehr ist — sonst reicht eine lange
    // Erklärung über den Fensterrand hinaus und ihr Ende ist unerreichbar.
    const unten = window.innerHeight - box.bottom - MARGIN
    const oben = box.top - MARGIN
    setPlace(unten < FLIP_BELOW && oben > unten
      ? { left, bottom: window.innerHeight - box.top + 6 }
      : { left, top: box.bottom + 6 })
  }

  function close() {
    setPinned(false)
    setPlace(null)
  }

  // Angetippt bleibt offen, bis woanders getippt oder Escape gedrückt wird.
  useEffect(() => {
    if (!pinned) return
    function onAway(event: MouseEvent) {
      if (!anchor.current?.contains(event.target as Node)) close()
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onAway)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onAway)
      window.removeEventListener('keydown', onKey)
    }
  }, [pinned])

  return (
    <button
      ref={anchor}
      type="button"
      className={s.icon}
      aria-label={`Erklärung zu ${term}`}
      onMouseEnter={measure}
      onMouseLeave={() => { if (!pinned) setPlace(null) }}
      onFocus={measure}
      onBlur={() => { if (!pinned) setPlace(null) }}
      onClick={(event) => {
        // Nicht mitsortieren: Das Zeichen sitzt im Spaltenkopf, und der ist
        // anklickbar.
        event.stopPropagation()
        if (pinned) close()
        else { setPinned(true); measure() }
      }}
    >
      ?
      {place && (
        <span className={s.popover} role="tooltip"
              style={{ top: place.top, bottom: place.bottom,
                       left: place.left }}>
          <span className={s.term}>{term}</span>
          {children}
        </span>
      )}
    </button>
  )
}
